import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import { useState } from 'react';
import SkeletonView from 'conversations-skeleton-state/components/SkeletonView';
import SkeletonText from 'conversations-skeleton-state/components/SkeletonText';
import ErrorMessageWithRetry from '../../shared/components/ErrorMessageWithRetry';
import { getAwayMode } from '../operators/getters';
import { useAgentStatus } from '../hooks/useAgentStatus';
import AgentStatusPrompt from './AgentStatusPrompt';
import AgentStatusToggle from './AgentStatusToggle';
import { useRealtimeAgentStatus } from '../hooks/useRealtimeAgentStatus';
import { TrackUsageProvider } from '../../usage-tracking/contexts/TrackUsageContext';
import { EndOutOfOfficePrompt } from './EndOutOfOfficePrompt';
import { useOutOfOfficeHours } from '../../working-hours/hooks/useOutOfOfficeHours';
import { CapacityLimits } from './CapacityLimits';
import { Flex } from 'foundations-components/components/Flex';
import { AvailabilityButton } from './AvailabilityPopover/AvailabilityButton';
import { useIsUngatedForUserLevelCapacityLimitsTicketType } from '../../auth/hooks/useGates';
import { HELPDESK } from '../../shared/constants/workspaces';
import { StatusMessage } from './StatusMessage/StatusMessage';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const AgentStatus = ({
  agentId,
  // TODO: replace by moving capacity limits fetchers/hook into routing-ui
  atCapacity,
  // TODO: replace by moving SetAgentStatusPrompt and contextual-prompts fetchers into routing-ui
  Prompt,
  usageTracker,
  showUserLevelCapacityLimits = false,
  workspace,
  objectTypeId: objectTypeIdProp
}) => {
  const isUngatedForUserLevelCapacityLimitsTicketType = useIsUngatedForUserLevelCapacityLimitsTicketType();
  let objectTypeId = objectTypeIdProp;
  if (!objectTypeId) {
    objectTypeId = workspace === HELPDESK ? 'TICKET' : 'CONVERSATION';
  }
  const {
    agentStatus,
    error,
    loadingAgentStatus,
    refetchAgentStatus,
    toggleAwayMode: updateAwayMode,
    updateAwayModeError
  } = useAgentStatus(usageTracker, workspace);
  useRealtimeAgentStatus(agentId, workspace);
  const awayModeState = agentStatus && getAwayMode(agentStatus);
  const {
    currentSegment,
    isLoadingOOOHours
  } = useOutOfOfficeHours(agentId, {
    isViewingSelf: true
  });
  const [showConfirmEndOutOfOffice, setShowConfirmEndOutOfOffice] = useState(false);
  if (loadingAgentStatus || isLoadingOOOHours) {
    return /*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      flexDirection: "row",
      inlineSize: "100%",
      justifyContent: "space-between",
      paddingInlineStart: "200",
      children: [/*#__PURE__*/_jsx(SkeletonText, {
        inlineSize: "100px"
      }), /*#__PURE__*/_jsx(SkeletonView, {
        blockSize: "26px",
        inlineSize: "52px"
      })]
    });
  }
  if (error || !agentStatus) {
    // Only degrade for errors critical to the function of agent status
    return /*#__PURE__*/_jsx(ErrorMessageWithRetry, {
      title: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: "routing-ui.agent-status.loadError"
      }),
      retry: refetchAgentStatus
    });
  }
  if (updateAwayModeError) {
    return /*#__PURE__*/_jsx(ErrorMessageWithRetry, {
      title: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: "routing-ui.agent-status.updateError"
      }),
      retry: () => updateAwayMode(!awayModeState)
    });
  }
  return /*#__PURE__*/_jsxs(TrackUsageProvider, {
    tracker: usageTracker,
    children: [showConfirmEndOutOfOffice && /*#__PURE__*/_jsx(EndOutOfOfficePrompt, {
      userId: agentId,
      isViewingSelf: true,
      onConfirm: () => {
        updateAwayMode(false);
        setShowConfirmEndOutOfOffice(false);
      },
      onReject: () => setShowConfirmEndOutOfOffice(false)
    }), /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      inlineSize: "100%",
      children: [/*#__PURE__*/_jsx(AgentStatusPrompt, {
        agentStatus: agentStatus,
        Prompt: Prompt,
        children: isUngatedForUserLevelCapacityLimitsTicketType ? /*#__PURE__*/_jsx(AvailabilityButton, {
          userId: agentId,
          isAway: !!awayModeState,
          updateAwayMode: awayMode => {
            if (currentSegment && !awayMode) {
              setShowConfirmEndOutOfOffice(true);
            } else {
              updateAwayMode(awayMode);
            }
          }
        }) : /*#__PURE__*/_jsx(AgentStatusToggle, {
          agentStatus: agentStatus,
          updateAwayMode: awayMode => {
            if (currentSegment && !awayMode) {
              setShowConfirmEndOutOfOffice(true);
            } else {
              updateAwayMode(awayMode);
            }
          },
          atCapacity: atCapacity,
          userId: agentId,
          currentSegment: currentSegment
        })
      }), isUngatedForUserLevelCapacityLimitsTicketType && /*#__PURE__*/_jsx(StatusMessage, {
        agentId: agentId,
        awayMode: !!awayModeState
      }), !isUngatedForUserLevelCapacityLimitsTicketType && showUserLevelCapacityLimits && /*#__PURE__*/_jsx(CapacityLimits, {
        agentId: agentId
      })]
    })]
  });
};
AgentStatus.displayName = 'AgentStatus';
export default AgentStatus;